import {visit} from 'unist-util-visit';
import type {Link, Parent, Root, Text} from 'mdast';

type RelativeGoBoardLinkOptions = {
    href: string;
    label: string;
};

/**
 * Replaces board-relative diagram directives with a link to the problem
 * position. List pages do not have the problem's board state, so rendering
 * these directives there could produce a diagram from an unrelated board.
 */
export function createRelativeGoBoardLinkTransform({
    href,
    label,
}: RelativeGoBoardLinkOptions): () => (tree: Root) => void {
    return () => (tree: Root) => {
        visit(tree, 'text', (node: Text, index, parent) => {
            if (!parent || index === undefined) return;

            const regex = /\[diagram:sgf=(root|here)/gi;
            const segments: (Text | Link)[] = [];
            let lastIndex = 0;
            let replaced = false;
            let match: RegExpExecArray | null;

            while ((match = regex.exec(node.value)) !== null) {
                const start = match.index;
                const directiveContentStart = start + match[0].length;
                let depth = 1;
                let directiveEnd = -1;

                for (
                    let cursor = directiveContentStart;
                    cursor < node.value.length;
                    cursor++
                ) {
                    if (node.value[cursor] === '[') depth++;
                    if (node.value[cursor] === ']') depth--;

                    if (depth === 0) {
                        directiveEnd = cursor;
                        break;
                    }
                }

                if (directiveEnd === -1) continue;

                if (start > lastIndex) {
                    segments.push({
                        type: 'text',
                        value: node.value.slice(lastIndex, start),
                    });
                }

                segments.push({
                    type: 'link',
                    url: href,
                    title: null,
                    children: [{type: 'text', value: label}],
                });

                replaced = true;
                lastIndex = directiveEnd + 1;
                regex.lastIndex = directiveEnd + 1;
            }

            if (!replaced) return;

            if (lastIndex < node.value.length) {
                segments.push({
                    type: 'text',
                    value: node.value.slice(lastIndex),
                });
            }

            (parent as Parent).children.splice(index, 1, ...segments);
        });
    };
}
